import { useNavigate, useSearchParams } from 'react-router-dom';
import { Caption1, Tab, TabList, makeStyles } from '@fluentui/react-components';
import { personDisplayName, type Person, type ProfileFact } from '@vigeo/shared';
import { StatusDot, complianceTokenFor, shellMedia, tokens } from '@vigeo/ui';
import { lifecyclePresenters, statusDetailLine } from '@/features/candidates';
import { complianceAndDocumentsShown } from '@/lib/sections';
import { formatPhone } from '@/features/roster';
import { ComplianceMark, useCompliance, useRoleOptions } from './data.js';
import { EditGuardProvider, useEditGuard } from './EditGuard.js';
import { Initials, serviceAreaWords, useProfileStyles } from './parts.js';
import { NOT_RECORDED, hireDay, roleLabels, tabLabels, tabOf, tabsShown, type ProfileTab } from './profile.js';
import { ComplianceTab, DocumentsTab, EmploymentTab, NotesTab, OverviewTab, PersonalTab, type TabProps } from './tabs.js';

const useStyles = makeStyles({
  page: { display: 'flex', flexDirection: 'column', gap: tokens.spacingVerticalL, minWidth: 0 },
  head: { paddingBottom: 0, gap: tokens.spacingVerticalL },
  who: { display: 'flex', alignItems: 'center', gap: tokens.spacingHorizontalL, flexWrap: 'wrap' },
  whoMain: { display: 'flex', flexDirection: 'column', gap: tokens.spacingVerticalXS, flexGrow: 1, minWidth: 0 },
  name: { margin: 0, fontSize: tokens.fontSizeHero700, lineHeight: tokens.lineHeightHero700, fontWeight: tokens.fontWeightSemibold, overflowWrap: 'anywhere' },
  meta: { display: 'flex', alignItems: 'center', gap: tokens.spacingHorizontalS, flexWrap: 'wrap', fontSize: tokens.fontSizeBase300, lineHeight: tokens.lineHeightBase300 },
  compliance: { display: 'flex', flexDirection: 'column', alignItems: 'flex-end', gap: tokens.spacingVerticalXS, [shellMedia.phone]: { alignItems: 'flex-start' } },
  facts: {
    display: 'grid', gridTemplateColumns: 'repeat(5, minmax(0, 1fr))', gap: tokens.spacingHorizontalXL, margin: 0,
    paddingBlock: tokens.spacingVerticalM, borderTop: `${tokens.strokeWidthThin} solid ${tokens.colorNeutralStroke2}`,
    [shellMedia.belowTablet]: { gridTemplateColumns: 'repeat(3, minmax(0, 1fr))' },
    [shellMedia.phone]: { gridTemplateColumns: 'repeat(2, minmax(0, 1fr))' },
  },
  fact: { display: 'flex', flexDirection: 'column', gap: tokens.spacingVerticalXXS, minWidth: 0 },
  factLabel: { margin: 0, color: tokens.colorNeutralForeground2, fontSize: tokens.fontSizeBase200, lineHeight: tokens.lineHeightBase200 },
  factValue: { margin: 0, fontSize: tokens.fontSizeBase300, lineHeight: tokens.lineHeightBase300, overflowWrap: 'anywhere' },
  tabs: { borderTop: `${tokens.strokeWidthThin} solid ${tokens.colorNeutralStroke2}`, overflowX: 'auto' },
});

export interface EmployeeProfileProps {
  readonly person: Person;
  /** What the candidate has typed or sent, projected onto this profile by the server on every read (ADR-013). Nothing where there is none. */
  readonly answers?: readonly ProfileFact[];
  /** Whether this user may change the record (`people.update`, Admin). The server decides; this only decides what is drawn. */
  readonly canEdit: boolean;
}

/**
 * One employee's profile (#585, Figma `1097:312`): a summary header, then six tabs holding the eight groups. The tab is in
 * the address as `?tab=`, beside the person's opaque id and nothing else about them.
 */
export function EmployeeProfile(props: EmployeeProfileProps) {
  // Keyed by person, so a form left open for one person can never be showing for the next.
  return <EditGuardProvider key={props.person.id}><Profile {...props} /></EditGuardProvider>;
}

function Profile({ person, canEdit, answers = [] }: EmployeeProfileProps) {
  const styles = useStyles();
  const shared = useProfileStyles();
  const guard = useEditGuard();
  const navigate = useNavigate();
  const [search] = useSearchParams();
  const withTracking = complianceAndDocumentsShown();
  const tab = tabOf(search.get('tab'), withTracking);
  const compliance = useCompliance(person, withTracking);
  const options = useRoleOptions();
  const roles = roleLabels(person, options.data?.jobProfiles);
  const display = personDisplayName(person.name);

  const go = (next: ProfileTab, edit?: string) => guard.request(() => {
    void navigate({ search: next === 'overview' ? '' : `?tab=${next}` });
    if (edit !== undefined && canEdit) guard.begin(edit);
  });
  const tabProps: TabProps = { person, answers, canEdit, compliance, roles, go };
  // The reason, effective day and note of the latest status change (#772), under the status they belong to.
  const statusLine = statusDetailLine(person.lifecycleDetail);
  const roleLine = [...roles, person.assignment?.positionTitle?.trim() ?? ''].filter((part) => part !== '').join(' · ');
  const facts: ReadonlyArray<readonly [string, string | null]> = [
    ['Cell phone', formatPhone(person.contact.phone) || null],
    ['Email', person.contact.email],
    ['Service areas', serviceAreaWords(person)],
    ['Supervisor', person.assignment?.supervisorName?.trim() || null],
    ['Hired', hireDay(person)],
  ];

  return (
    <div className={styles.page}>
      <header className={`${shared.card} ${styles.head}`}>
        <div className={styles.who}>
          <Initials person={person} />
          <div className={styles.whoMain}>
            <h1 className={styles.name}>{display}</h1>
            <div className={styles.meta}>
              <StatusDot color={person.lifecycle === 'active' ? complianceTokenFor.good : tokens.colorNeutralForeground3}>{lifecyclePresenters[person.lifecycle].label}</StatusDot>
              <span aria-hidden="true" className={shared.quiet}>·</span>
              <span>{roleLine === '' ? 'No role set' : roleLine}</span>
            </div>
            {statusLine === null ? null : <div className={shared.quiet}>{statusLine}</div>}
          </div>
          {withTracking ? (
            <div className={styles.compliance}>
              <Caption1>Compliance</Caption1>
              <ComplianceMark compliance={compliance} />
            </div>
          ) : null}
        </div>
        <dl className={styles.facts} aria-label="Key facts">
          {facts.map(([label, value]) => (
            <div key={label} className={styles.fact}>
              <dt className={styles.factLabel}>{label}</dt>
              <dd className={`${styles.factValue} ${value === null ? shared.empty : ''}`}>{value ?? NOT_RECORDED}</dd>
            </div>
          ))}
        </dl>
        <TabList className={styles.tabs} selectedValue={tab} onTabSelect={(_, data) => go(data.value as ProfileTab)} aria-label="Profile sections">
          {tabsShown(withTracking).map((value) => <Tab key={value} id={`profile-tab-${value}`} value={value}>{tabLabels[value]}</Tab>)}
        </TabList>
      </header>
      <div role="tabpanel" aria-labelledby={`profile-tab-${tab}`}>
        {tab === 'overview' ? <OverviewTab {...tabProps} />
          : tab === 'personal' ? <PersonalTab {...tabProps} />
            : tab === 'employment' ? <EmploymentTab {...tabProps} />
              : tab === 'compliance' ? <ComplianceTab {...tabProps} />
                : tab === 'documents' ? <DocumentsTab {...tabProps} />
                  : <NotesTab {...tabProps} />}
      </div>
    </div>
  );
}
